<template>
  <div class="contact-form" :class="className" :id="`contact-form-${uid}`">
    <transition mode="out-in" name="contact-form__fade">
      <form
        method="post"
        v-if="!formSent"
        :name="formName"
        @submit.prevent="submitForm()"
      >
        <RequestType
          v-if="showRequestType"
          v-model:request-type="requestType"
          :request-types="requestTypes"
        />

        <transition name="contact-form__reveal-animation">
          <therapy-choices v-if="showTherapyChoices" />
        </transition>

        <transition name="contact-form__reveal-animation">
          <meeting-type
            class="contact-form__select-meeting-type"
            v-if="showMeetingType"
            v-model:meeting-type="meetingType"
            :request-type="requestType"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <contact-form-city-select
            class="form-row contact-form__city"
            v-if="showCitySelect"
            ref="citySelect"
            :clearable="true"
            :default="selectedCity"
            :required="isCityRequired"
            :select-on-tab="true"
            @on-select="selectCity"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <case-type-select
            v-if="showCaseTypeSelect"
            :options="competenceOptions"
            :request-type="requestType"
            @update-case-type="updateCaseType"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <contact-info
            v-if="showContactInfo"
            ref="contactInfo"
            v-model:name="name"
            v-model:email="email"
            v-model:phone="phone"
            v-model:message="message"
            :email-is-required="therapyOrderEmailIsRequired"
            :message-is-required="messageIsRequired"
            :request-type="requestType"
            @trigger-field-error="triggerFieldError"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <coupon-code
            v-if="showCouponCode"
            v-model:coupon="coupon"
            :check-coupon="checkCouponWrapper"
            :valid-coupon="validCoupon"
            @trigger-field-error="triggerFieldError"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <submit-section
            v-if="!showTherapyChoices"
            v-model:accept="accept"
            :accept-text="texts.contactFormAcceptText"
            :error="error"
            :has-errors="hasErrors"
            :is-disabled="isDisabled"
            :loading="isLoading"
            :show-accept-text="showAcceptText"
            :submit-button-text="texts.submit"
          />
        </transition>
      </form>

      <form-success-message
        v-else
        :name="name"
        :request-type="requestType"
      />
    </transition>

    <loader :loading="isLoading" />
  </div>
</template>

<script>
import Loader from 'Components/loader/Loader.vue';
import { useContactForm } from 'Composables/useContactForm';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import CaseTypeSelect from 'Main/contact-form/components/CaseTypeSelect.vue';
import ContactFormCitySelect from 'Main/contact-form/components/ContactFormCitySelect.vue';
import ContactInfo from 'Main/contact-form/components/ContactInfo.vue';
import CouponCode from 'Main/contact-form/components/CouponCode.vue';
import FormSuccessMessage from 'Main/contact-form/components/FormSuccessMessage.vue';
import MeetingType from 'Main/contact-form/components/MeetingType.vue';
import RequestType from 'Main/contact-form/components/RequestType.vue';
import SubmitSection from 'Main/contact-form/components/SubmitSection.vue';
import {
  FIELD_ORDER,
  isTherapyRequest,
  MEETING_TYPE_KEYS,
  REQUEST_TYPES,
} from 'Main/contact-form/constants';
import TherapyChoices from 'Main/contact-slide-in/components/TherapyChoices.vue';
import { useId } from 'vue';
import { mapActions, mapState } from 'vuex';

export default {

  components: {
    CaseTypeSelect,
    ContactFormCitySelect,
    ContactInfo,
    CouponCode,
    FormSuccessMessage,
    Loader,
    MeetingType,
    RequestType,
    SubmitSection,
    TherapyChoices,
  },

  computed: {
    ...mapState('contactForm', ['isLoading', 'isDisabled']),
    ...mapState('cities', ['cityLinks']),
    ...mapState('inquiryCompetenceAreas', ['competenceCategoryTypeOptions']),

    competenceOptions() {
      if (this.competenceList && this.competenceList.length) {
        return this.competenceList.map((competenceAreaName) => {
          return { label: competenceAreaName, value: competenceAreaName };
        });
      }

      return [];
    },

    contactVia() {
      if (this.pageTitle) {
        return this.pageTitle;
      }

      return this.datasetPageTitle;
    },

    formName() {
      const name = `contact-form-${this.requestType}`;

      if (this.propFormName) {
        return `${name}-${this.propFormName}`;
      }

      return name;
    },

    isCityRequired() {
      return this.showCitySelect;
    },

    isDisabled() {
      return !this.showAcceptText || !this.accept;
    },

    messageIsRequired() {
      return this.requestType === REQUEST_TYPES.LAW;
    },

    messagePlaceHolder() {
      if (this.messageIsRequired) {
        return this.texts.messageRequiredPlaceholder;
      }

      return isTherapyRequest(this.requestType)
        ? this.texts.messageNotRequiredPlaceholderTherapy
        : this.texts.messageNotRequiredPlaceholder;
    },

    missingFields() {
      let requiredFields = ['name', 'phone', 'accept', 'requestType'];

      if (this.showCitySelect && this.isCityRequired) {
        requiredFields = requiredFields.concat(['selectedCity']);
      }

      if (this.showCaseTypeSelect) {
        requiredFields = requiredFields.concat(['caseType']);
      }

      if (this.messageIsRequired) {
        requiredFields = requiredFields.concat(['message']);
      }

      if (this.therapyOrderEmailIsRequired) {
        requiredFields = requiredFields.concat(['email', 'meetingType']);
      }

      return requiredFields
        .filter((field) => !this[field])
        .sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
    },

    pagePostType() {
      if (this.postType) {
        return this.postType;
      }

      return this.datasetPostType;
    },

    selectedCity() {
      if (!this.cityTerm) {
        return null;
      }

      return { choice: this.cityTerm.id, label: this.cityTerm.name };
    },

    showAcceptText() {
      if (isTherapyRequest(this.requestType)) {
        return this.showContactInfo;
      }
      return true;
    },

    showCaseTypeSelect() {
      return (
        this.therapyOrderWithoutPayment ||
        this.requestType === REQUEST_TYPES.LAW
      );
    },

    showCitySelect() {
      if (this.disableCitySelectContactForm || this.showTherapyChoices) {
        return false;
      }

      return (
        (this.cityInternallySelected ||
          !this.selectedCity ||
          this.alwaysShowCitySelect) &&
        this.meetingType !== MEETING_TYPE_KEYS.VIDEO
      );
    },

    showContactInfo() {
      return (
        !isTherapyRequest(this.requestType) ||
        this.therapyOrderWithoutPayment
      );
    },

    showCouponCode() {
      if (
        this.currentSite.toUpperCase() === 'DK' ||
        !isTherapyRequest(this.requestType)
      ) {
        return false;
      }
      return this.showContactInfo;
    },

    showMeetingType() {
      if (this.disableMeetingTypeContactForm) {
        return false;
      }

      return this.therapyOrderWithoutPayment;
    },

    showTherapyChoices() {
      return (
        isTherapyRequest(this.requestType) && !this.createWithoutPayment
      );
    },

    therapyOrderEmailIsRequired() {
      return (
        isTherapyRequest(this.requestType) && !this.emailIsOptional
      );
    },

    therapyOrderWithoutPayment() {
      return (
        isTherapyRequest(this.requestType) && this.createWithoutPayment
      );
    },
  },

  created() {
    this.caseType = null;
    if (window.contactFormContent) {
      this.message = window.contactFormContent;
    }
  },

  data() {
    return {
      adminMarket: theme_vars.currentAdminMarket,
      cityInternallySelected: false,
      coupon: '',
      datasetPageTitle: this.$mountEl.dataset.pageTitle
        ? this.$mountEl.dataset.pageTitle
        : '',
      datasetPostType: this.$mountEl.dataset.postType
        ? this.$mountEl.dataset.postType
        : '',
      disableCitySelectContactForm: theme_vars.disableCitySelectContactForm,
      disableMeetingTypeContactForm: theme_vars.disableMeetingTypeContactForm,
      meetingType: theme_vars.disableMeetingTypeContactForm
        ? MEETING_TYPE_KEYS.VIDEO
        : null,
      requestType: '',
      requestTypes: theme_vars.siteRequestTypesList,
      showRequestType: true,
      siteId: theme_vars.currentSiteId,
      texts: theme_vars.contactTexts,
      validate: ['name', 'phone', 'email'],
    };
  },

  methods: {
    ...mapActions('contactForm', ['setIsDisabled']),

    checkCouponWrapper() {
      return this.checkCoupon(this.coupon, '', this.email, this.texts);
    },

    resetForm() {
      this.name = '';
      this.phone = '';
      this.email = '';
      this.message = '';
      this.coupon = '';
      this.caseType = null;
      this.meetingType = theme_vars.disableMeetingTypeContactForm
        ? MEETING_TYPE_KEYS.VIDEO
        : null;
      this.cityInternallySelected = false;
      this.cityTerm = null;
      this.showRequestType = true;
    },

    selectCity(value) {
      this.cityInternallySelected = true;
      this.cityTerm = value ? { id: value.choice, name: value.label } : null;

      this.saveCityTermToLocalStorage();
      if (this.value) {
        this.$store.dispatch('cities/getCityLink', value ? value.choice : '');
      }
    },

    setRequestType() {
      // If there is only one request type, set it and hide the request type selector
      if (this.requestTypes && Object.keys(this.requestTypes).length === 1) {
        this.requestType = Object.keys(this.requestTypes)[0];
        this.showRequestType = false;
      }

      switch (this.pagePostType) {
        case 'city':
          this.requestType = 'funeral';
          this.showRequestType = false;
          break;
        case 'jurist':
          this.requestType = 'law';
          this.showRequestType = false;
          break;
        case 'therapy':
        case 'therapist':
        case 'couples-therapist':
        case 'talk-therapy':
        case 'psychologist':
        case 'family-counseling':
        case 'coach':
        case 'cb-therapy':
          this.requestType = 'therapy';
          this.showRequestType = false;
          break;
        case 'tombstone':
          this.requestType = 'tombstone';
          this.showRequestType = false;
          break;
        default:
          break;
      }
    },

    async submitForm() {
      await this.sendInquiry({
        contactInfoRef: this.$refs.contactInfo,
        contactVia: this.contactVia,
        coupon: this.coupon,
        createWithoutPayment: this.createWithoutPayment,
        formName: this.formName,
        formSent: this.formSent,
        meetingType: this.meetingType,
        missingFields: this.missingFields,
        onFormSent: (val) => {
          this.$emit('update:formSent', val);
        },
        price: this.price,
        refs: this.$refs,
        requestType: this.requestType,
        rootEl: this.$mountEl,
        selectedCity: this.selectedCity,
        tags: this.tags,
        texts: this.texts,
        validateRefs: this.validate,
      });
    },

    updateCaseType(val = '') {
      this.caseType = val;
    },
  },

  mounted() {
    this.setRequestType();
  },
  name: 'ContactForm',

  props: {
    alwaysShowCitySelect: { default: false, type: Boolean },
    className: { default: '', type: String },
    competenceList: { default: () => [], type: Array },
    createWithoutPayment: { default: false, type: Boolean },
    emailIsOptional: { default: false, type: Boolean },
    formSent: { default: false, type: Boolean },
    pageTitle: { default: '', type: String },
    postType: { default: '', type: String },
    propFormName: { default: '', type: String },
    tags: { default: () => [], type: Array },
  },

  setup() {
    const uid = useId();
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    const contactForm = useContactForm();
    return {
      cityTerm,
      getCityTermFromLocalStorage,
      saveCityTermToLocalStorage,
      uid,
      ...contactForm,
    };
  },

  watch: {

    cityLinks() {
      this.cityTerm = this.getCityTermFromLocalStorage();
    },
    requestType(newRequestType) {
      if (isTherapyRequest(newRequestType)) {
        this.meetingType = MEETING_TYPE_KEYS.VIDEO;
        return;
      }

      this.meetingType = '';
    },
  },
};
</script>
